import { z } from 'zod'
import React from 'react'
import { FormSection } from '@/common/components/Form/FormSection.tsx'
import { FormInput } from '@/common/components/Form/FormInput.tsx'
import { dictToSelectOptions } from '@/utils/misc.ts'
import { Gender } from '@/common/types/misc.def.ts'
import { FormSelectAntd } from '@/common/components/Form/FormSelectAntd.tsx'

export type PersonalInfoSchemaBuildArgs = {
  needGovId?: boolean
  needAddress?: boolean
  needNationality?: boolean
  needGender?: boolean
  needCn?: boolean
  needEmail?: boolean
  needPreferredName?: boolean
}

export const buildPersonalInfoSchema = (args?: PersonalInfoSchemaBuildArgs) => {
  return z.object({
    fullName: z.string().min(1),
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
    preferredName: args?.needPreferredName ? z.string() : z.string().optional(),
    cnFullName: z.string().min(1).optional(),
    govId: z.string().optional(),
    gender: args?.needGender ? z.nativeEnum(Gender) : z.nativeEnum(Gender).optional(),
    address: args?.needAddress ? z.string().min(1) : z.string().min(1).optional(),
    email: args?.needEmail ? z.string().min(1) : z.string().min(1).optional(),
    nationality: z.string().min(1).optional(),
    cnAddress: z.string().min(1).optional(),
    cnNationality: z.string().min(1).optional(),
  }).superRefine((data, ctx) => {
    if (args?.needCn) {
      if (!data.cnFullName) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['cnFullName'] })
      }
    }

    if (args?.needAddress && !data.address) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['address'] })
    }

    if (args?.needNationality && !data.nationality) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['nationality'] })
    }

    if (args?.needCn && args?.needAddress && !data.cnAddress) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['cnAddress'] })
    }

    if (args?.needCn && args?.needNationality && !data.cnNationality) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['cnNationality'] })
    }

    if (args?.needGender && !data.gender) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['gender'] })
    }
  })
}

export const PersonalInfoSection: React.FC<PersonalInfoSchemaBuildArgs> = ({
  needGovId,
  needAddress,
  needNationality,
  needGender,
  needCn,
  needEmail,
  needPreferredName = true,
}) => {
  return (
    <FormSection name="personalInfo">
      {needPreferredName && <FormInput name="preferredName" />}
      <FormInput name="fullName" description="Legal full name" />
      {needCn && <FormInput name="cnFullName" description="Legal full name" />}
      {needGovId && <FormInput name="govId" description="optional" />}
      {needAddress && <FormInput name="address" />}
      {needCn && needAddress && <FormInput name="cnAddress" />}
      {needNationality && <FormInput name="nationality" />}
      {needCn && needNationality && <FormInput name="cnNationality" />}
      {needGender && <FormSelectAntd
        name="gender"
        options={dictToSelectOptions(Gender)}
      />}
      {needEmail && <FormInput name="email" />}
    </FormSection>
  )
}